import type {
    CreateExtArgs,
    CreateExtensionArgument,
    DalValue,
    DmApis,
    Extension,
    ExtensionAPI,
    ValidatorMap
} from '@wix/document-manager-core'
import type {
    AutoGridCalculationConfig,
    AutoGridCalculationResults,
    CompRef,
    Pair,
    Pointer,
    Rect
} from '@wix/document-services-types'
import {calculateGridByRects} from './generators/autoGrid/autoGrid'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {RMApi} from '../rendererModel'
import {DATA_TYPES, VIEW_MODES} from '../../constants/constants'
import type {SchemaExtensionAPI} from '../schema/schema'

interface LayoutAPI extends ExtensionAPI {
    generators: {
        autoGrid: {
            generate(
                containerRect: Rect,
                childPointerRectPairs: Pair<CompRef, Rect>[],
                config?: AutoGridCalculationConfig
            ): AutoGridCalculationResults
        }
    }
}

interface ComponentsAPI extends ExtensionAPI {
    isPinned(pointer: Pointer): boolean
}

export interface LayoutExtensionApi extends ExtensionAPI {
    layout: LayoutAPI
    components: ComponentsAPI
}

const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const createValidator = ({extensionAPI, pointers}: DmApis): ValidatorMap => {
        const validateBuilderComponentLayoutQuery = (pointer: Pointer, value: DalValue) => {
            if (!value || pointer.type !== VIEW_MODES.DESKTOP || pointers.structure.isMasterPage(pointer)) {
                return
            }

            const {rendererModel, schemaAPI} = extensionAPI as RMApi & SchemaExtensionAPI

            if (
                !rendererModel.isBuilderComponentModel() ||
                !value.componentType ||
                !schemaAPI.isSchemaCollectionRegistered(value.componentType)
            ) {
                return
            }

            if (!value.layoutQuery) {
                return [
                    {
                        shouldFail: experimentInstance.isOpen('dm_failOnMissingLayoutQuery'),
                        type: 'missingLayoutQueryForComponent',
                        message: `Component ${pointer.id} is missing layout`,
                        extras: {
                            pointer,
                            componentType: value.componentType
                        }
                    }
                ]
            }
        }

        return {
            validateBuilderComponentLayoutQuery
        }
    }

    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): LayoutExtensionApi => {
        return {
            layout: {
                generators: {
                    autoGrid: {
                        generate: (
                            containerRect: Rect,
                            childPointerRectPairs: Pair<CompRef, Rect>[],
                            config?: AutoGridCalculationConfig
                        ) => {
                            const results = calculateGridByRects(
                                containerRect,
                                Object.fromEntries(
                                    childPointerRectPairs.map(([compPointer, rect]) => [compPointer.id, rect])
                                ),
                                {
                                    ...config,
                                    componentsConfig: Object.fromEntries(
                                        config?.componentsConfig?.map(([compPointer, compConfig]) => [
                                            compPointer.id,
                                            compConfig
                                        ]) ?? []
                                    )
                                }
                            )

                            const {componentsLayouts, containerLayout} = results

                            return {
                                containerLayout,
                                componentsLayouts: childPointerRectPairs.map(([compPointer]) => [
                                    compPointer,
                                    componentsLayouts[compPointer.id]
                                ])
                            }
                        }
                    }
                }
            },

            components: {
                isPinned: (compPointer: Pointer) => {
                    const layout = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
                        compPointer,
                        DATA_TYPES.layout
                    )
                    return (
                        layout?.itemLayout?.type === 'FixedItemLayout' || layout?.itemLayout?.pinnedToContainer === true
                    )
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {layout} = extensionAPI as LayoutExtensionApi

        return {
            layout: {
                generators: {
                    autoGrid: {
                        generate: layout.generators.autoGrid.generate
                    }
                }
            }
        }
    }
    return {
        name: 'layout',
        createExtensionAPI,
        createPublicAPI,
        createValidator
    }
}

export {createExtension}
